import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pendingAccountApi } from '../../services/admin/adminApi'
import { PendingAccountCreateRequest } from '../../types/admin'
import { toast } from 'sonner'

/**
 * React Query hook for fetching all pending accounts
 * @returns Query result with pending account list
 */
export function usePendingAccounts() {
  return useQuery({
    queryKey: ['admin', 'pending-accounts'],
    queryFn: () => pendingAccountApi.list(),
    select: (response) => response.data || [],
  })
}

/**
 * React Query mutation hook for deleting pending accounts
 * Invalidates pending accounts list on success
 * @returns Mutation result for pending account deletion
 */
export function useDeletePendingAccounts() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => pendingAccountApi.delete(ids),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-accounts'] })
      toast.success(`Successfully deleted ${ids.length} pending ${ids.length === 1 ? 'account' : 'accounts'}`)
    },
    onError: (error: any) => {
      console.error('Failed to delete pending accounts:', error)
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to delete pending accounts')
    },
  })
}

/**
 * React Query mutation hook for resending invitation to a pending account
 * Invalidates pending accounts list on success
 * @returns Mutation result for resending invitation
 */
export function useResendInvite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => pendingAccountApi.resendInvite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-accounts'] })
      toast.success('Invitation resent successfully')
    },
    onError: (error: any) => {
      console.error('Failed to resend invitation:', error)
      toast.error(error?.response?.data?.error?.message || error.message || 'Failed to resend invitation')
    },
  })
}

/**
 * React Query mutation hook for creating a pending account invitation
 * Invalidates pending accounts list on success
 * @returns Mutation result for creating invitation
 */
export function useCreatePendingAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: PendingAccountCreateRequest) => pendingAccountApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'pending-accounts'] })
      toast.success(response.data?.message || 'Invitation sent successfully')
    },
    onError: (error: any) => {
      console.error('Failed to create pending account:', error)
      const message = error?.response?.data?.error?.message || error.message || 'Failed to send invitation'
      toast.error(message)
    },
  })
}
